import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import { useLanguage } from "../i18n/LanguageContext";
import { Loader2 } from "lucide-react";
import { useClinicalShellTheme } from "../hooks/useBrandShellTheme";
import { clearPkceVerifiers } from "../lib/pkceStorage";

const completeLogin = (accessToken, refreshToken, login, navigate) => {
  return fetch(`${API}/auth/me`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  })
    .then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    })
    .then((data) => {
      if (data?.data) {
        login(data.data, { access_token: accessToken, refresh_token: refreshToken });
        navigate("/case/new", { replace: true });
      } else {
        throw new Error("No user data");
      }
    });
};

/** Wait for a session that detectSessionInUrl / a peer exchange may still be writing. */
async function waitForSupabaseSession(supabase, timeoutMs = 4000) {
  const immediate = await supabase.auth.getSession();
  if (immediate.data?.session?.access_token) {
    return immediate.data.session;
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (session) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      subscription.unsubscribe();
      resolve(session);
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (
        session?.access_token &&
        (event === "SIGNED_IN" || event === "INITIAL_SESSION" || event === "TOKEN_REFRESHED")
      ) {
        finish(session);
      }
    });

    const timer = setTimeout(async () => {
      const late = await supabase.auth.getSession();
      finish(late.data?.session ?? null);
    }, timeoutMs);
  });
}

const AuthCallbackPage = () => {
  useClinicalShellTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const { t } = useLanguage();
  const processed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const handleFailure = (err) => {
      console.error("Auth callback failed:", err);
      setError(err?.message || "Auth failed");
      localStorage.removeItem("mc_access_token");
      localStorage.removeItem("mc_refresh_token");
      setTimeout(() => navigate("/login?error=auth_failed", { replace: true }), 2000);
    };

    const run = async () => {
      // CIS Yandex OAuth — tokens in query string
      const queryAccess = searchParams.get("access_token");
      const queryRefresh = searchParams.get("refresh_token");
      if (queryAccess) {
        try {
          await completeLogin(queryAccess, queryRefresh, login, navigate);
        } catch (err) {
          handleFailure(err);
        }
        return;
      }

      // US Supabase OAuth — PKCE code or existing session
      const { supabase } = await import("../lib/supabase");
      if (!supabase) {
        navigate("/login?error=no_token", { replace: true });
        return;
      }

      const oauthError = searchParams.get("error");
      if (oauthError) {
        handleFailure(
          new Error(searchParams.get("error_description") || oauthError)
        );
        return;
      }

      try {
        const code = searchParams.get("code");
        if (code) {
          const { data, error: exchangeError } =
            await supabase.auth.exchangeCodeForSession(code);
          if (!exchangeError && data?.session?.access_token) {
            clearPkceVerifiers(window.localStorage);
            await completeLogin(
              data.session.access_token,
              data.session.refresh_token,
              login,
              navigate
            );
            return;
          }
          // First Apple login is slow; the client may already have exchanged the
          // one-time code (or still be finishing). Wait before treating as failure.
          const session = await waitForSupabaseSession(supabase);
          if (session?.access_token) {
            clearPkceVerifiers(window.localStorage);
            await completeLogin(
              session.access_token,
              session.refresh_token,
              login,
              navigate
            );
            return;
          }
          throw exchangeError || new Error("No session after OAuth callback");
        }

        const session = await waitForSupabaseSession(supabase, 2000);
        if (session?.access_token) {
          await completeLogin(
            session.access_token,
            session.refresh_token,
            login,
            navigate
          );
          return;
        }

        navigate("/login?error=no_token", { replace: true });
      } catch (err) {
        handleFailure(err);
      }
    };

    run();
  }, [searchParams, navigate, login]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center" data-testid="auth-callback-page">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        {error ? (
          <p className="text-destructive text-sm">{t("auth.authError")}: {error}</p>
        ) : (
          <p className="text-muted-foreground text-sm">{t("auth.authorizing")}</p>
        )}
      </div>
    </div>
  );
};

export default AuthCallbackPage;
